import { useTranslation } from 'react-i18next';
import { useAppContext, useAsyncData } from '@/hooks';
import { api } from '@/api/client';
import type { Product } from '@/types';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/icons';
import styles from './AdminProductsPage.module.css';

const STATUS_CLASS: Record<string, string> = {
  AVAILABLE: 'statusAvailable',
  SETUP_REQUIRED: 'statusSetupRequired',
  NOT_SUBSCRIBED: 'statusNotSubscribed',
};

const STATUS_KEY: Record<string, string> = {
  AVAILABLE: 'admin.products.available',
  SETUP_REQUIRED: 'admin.products.setupRequired',
  NOT_SUBSCRIBED: 'admin.products.notSubscribed',
};

export function AdminProductsPage() {
  const { t } = useTranslation();
  const { activeContext } = useAppContext();
  const institutionId = activeContext?.institution?.id ?? '';

  const { state, reload } = useAsyncData<Product[]>(
    `admin-products-${institutionId}`,
    () => api.admin.products.list(institutionId),
    !!institutionId,
  );

  const products = state.status === 'ready' ? state.data : [];

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.headerName}>{t('admin.products.title')}</h1>
          <p className={styles.headerSubtitle}>{t('admin.products.subtitle')}</p>
        </div>
      </header>

      {state.status === 'loading' && (
        <div className={styles.loading}>{t('admin.products.loading')}</div>
      )}

      {state.status === 'error' && (
        <Card className={styles.emptyState}>
          <Icon name="error" size={48} />
          <h2 className={styles.emptyStateTitle}>{t('admin.products.title')}</h2>
          <p className={styles.emptyStateBody}>{t('common.error')}</p>
          <button className={styles.retryButton} onClick={reload}>{t('common.retry')}</button>
        </Card>
      )}

      {state.status === 'ready' && products.length === 0 && (
        <Card className={styles.emptyState}>
          <Icon name="noProducts" size={48} />
          <h2 className={styles.emptyStateTitle}>{t('admin.products.noProducts')}</h2>
          <p className={styles.emptyStateBody}>{t('products.noProductsDesc')}</p>
        </Card>
      )}

      {state.status === 'ready' && products.length > 0 && (
        <div className={styles.productGrid}>
          {products.map((product) => (
            <Card key={product.code} className={styles.productCard}>
              <div className={styles.productIcon}>
                <Icon name={(product.icon as never) ?? 'products'} size={24} />
              </div>
              <h3 className={styles.productName}>{product.name}</h3>
              <p className={styles.productDescription}>{product.description}</p>
              <span className={`${styles.statusBadge} ${styles[STATUS_CLASS[product.status] ?? 'statusNotSubscribed']}`}>
                {t(STATUS_KEY[product.status] ?? product.status)}
              </span>
              {/*
                This used to be a "Configure" button with onClick={() => {}} — a dead
                control that swallowed every click. There is nothing an institution
                administrator configures on this card: launch configuration, mappings
                and activation are platform-administrator acts. So the card offers the
                one action that IS theirs — opening the product — and states the
                blocked cases in words instead of a button that pretends.
              */}
              <div className={styles.cardActions}>
                {product.status === 'AVAILABLE' && product.launchUrl && (
                  <Button
                    size="sm"
                    onClick={() => window.open(product.launchUrl, '_blank', 'noopener,noreferrer')}
                  >
                    {t('admin.products.open')} <Icon name="external" size={14} aria-hidden="true" />
                  </Button>
                )}
                {product.status === 'AVAILABLE' && !product.launchUrl && (
                  <p className={styles.cardNote}>{t('admin.products.launchNotConfigured')}</p>
                )}
                {product.status === 'SETUP_REQUIRED' && (
                  <p className={styles.cardNote}>{t('admin.products.setupInProgress')}</p>
                )}
                {product.status === 'NOT_SUBSCRIBED' && (
                  <p className={styles.cardNote}>{t('admin.products.notSubscribedNote')}</p>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
